import { Icon } from './Icon'
import { useDialog } from '../useDialog'
import { TOURS } from './Tour'
import { PLACES } from '../router'
import { PLACE_NAMES } from '../router'
import type { Place } from '../router'
import { guidesFor, guideUrl } from '../client-guides'

/**
 * Which tour belongs to which screen — derived, not listed.
 *
 * Not every place has one: a tour of the Redirects screen would be one stop
 * saying what a redirect is, which is what the sentence above it already
 * does. The screens that earn a walk are the ones with several controls whose
 * relationship is not obvious, and `TOURS` is where that judgement is
 * recorded.
 *
 * It WAS listed — pages, pulse, settings — and the day a tour was written for
 * Home the list would have made it `ELSEWHERE`, described to a reader as a
 * tour "about a surface that is not a place". A place with a tour is a place
 * with a tour; the two lists cannot disagree if there is one.
 */
const TOUR_FOR: Partial<Record<Place, string>> = Object.fromEntries(
  PLACES.filter((place) => place in TOURS).map((place) => [place, place]),
)

/**
 * The tours that belong to a panel rather than to a screen.
 *
 * Read from `TOURS` rather than typed again, so a tour added there is
 * reachable the day it is written: anything with no place of its own is one
 * of these.
 */
const ELSEWHERE = Object.keys(TOURS).filter((surface) => !Object.values(TOUR_FOR).includes(surface))

/**
 * Help, about the screen somebody is actually on.
 *
 * A help button that opens a keyboard-shortcut table is a help button that
 * answers a question nobody asked: the person pressing it is stuck on THIS
 * screen, and the shortcuts are the one thing they were not wondering about.
 *
 * So the panel leads with three sentences about where they are, keyed by the
 * place — what the screen is for, the thing people usually want next, and the
 * one rule that surprises them — and keeps the shortcuts a press away for the
 * people who did come for those.
 */

interface Guidance {
  what: string
  next: string
  /** The rule that catches people out. Every screen has exactly one. */
  gotcha: string
}

const ON_THIS_SCREEN: Record<Place, Guidance> = {
  home: {
    what: 'Whether the documentation still matches the product, at a glance: what is stale, what readers could not find, and what is waiting for you.',
    next: 'Open Pages and write. Every number here comes from work done there.',
    gotcha:
      'The counts describe the PUBLISHED site, not your unpublished changes — so a page you have just fixed stays listed until you publish it.',
  },
  pulse: {
    what: 'Whether each page is still true. Pulse watches how long ago a page was written, what changed around it, and what readers did next.',
    next: 'Open a page it names and either fix it or mark it reviewed — both answer the question it is asking.',
    gotcha:
      'Marking a page reviewed is a promise about today, not a repair. It resets the clock and changes nothing on the page.',
  },
  analytics: {
    what: 'What readers actually did: which pages they read, what they searched for, and which searches found nothing.',
    next: 'Read the searches that found nothing — each one is a page somebody wanted and you do not have.',
    gotcha:
      'These are honest counts of reading, with no cookies and no addresses, so they will not match a marketing analytics tool.',
  },
  search: {
    what: 'What a reader’s search finds when the ranking on its own is not enough: words that mean each other, one search that means another, the searches with a known answer, and the pages worth more or less everywhere.',
    next: 'Start from Pulse. The searches that found nothing are where almost every entry here comes from.',
    gotcha:
      'None of it can hide a page — a weight moves a page up or down and never removes it — and none of it shows on the page it affects, so this screen is the only record of what has been tuned.',
  },
  pages: {
    what: 'The documentation itself: the tree on the left is what readers see, and the middle is the page.',
    next: 'Type. Your changes are kept apart from the live site until you press Publish.',
    gotcha:
      'A page the navigation does not list cannot be published, which is why the New page dialog asks where it goes.',
  },
  import: {
    what: 'Bringing documentation in from GitBook, Docusaurus, Mintlify, Notion or a folder of Markdown.',
    next: 'Drop the export in. The report afterwards names every page it could not carry whole.',
    gotcha:
      'An import lands as changes, not on the live site — nothing readers see moves until you publish.',
  },
  library: {
    what: 'Every file the pages share, and where each one is used.',
    next: 'Give anything without alt text a description — it is what a screen reader reads out.',
    gotcha:
      'Deleting a file that a page still uses is refused, and the refusal names the pages, so nothing breaks quietly.',
  },
  variables: {
    what: 'Words and blocks the whole site shares: a product name, a version number, a warning repeated on nine pages.',
    next: 'Add a value, then write it into a page — one edit here changes every page that uses it.',
    gotcha:
      'Variables ride your changes into publishing like any page, so a value edited here is not live until you publish.',
  },
  redirects: {
    what: 'Where old addresses send readers, so a link somebody saved two years ago still lands somewhere.',
    next: 'Add the address that used to work, and the page it should reach now.',
    gotcha:
      'Renaming a page writes one of these for you — this screen is for addresses that never existed here.',
  },
  words: {
    what: 'The word this documentation uses for a thing, the ones it does not, and the short rules that say how it is written.',
    next: 'Add the three names your product has for the same thing, and pick one.',
    gotcha:
      'It is a warning to a writer, never a refusal — and the words you replace become search aliases, so a reader finds the page either way.',
  },
  settings: {
    what: 'Everything about this site and this workspace: who can read it, what it looks like, where it answers, and who is in it.',
    next: 'Connect a domain, or invite the people who write with you.',
    gotcha:
      'Some of these are per SITE and some are per workspace — the card says which, because a workspace can hold several sites.',
  },
  developers: {
    what: 'The two ways software works with Staleless: MCP gives an AI agent scoped access, while the CLI checks documentation locally and in CI.',
    next: 'Choose MCP or CLI, then copy the setup shown for that tool.',
    gotcha:
      'A token is shown once. MCP can stage and commit a draft when scoped to do so, but it can never publish.',
  },
  help: {
    what: 'The client guides for setting up, writing, publishing, managing and connecting Staleless.',
    next: 'Search for the task you are trying to complete, then open the matching guide.',
    gotcha:
      'Guides open on the published documentation site, so the address can be shared with somebody who is not in this workspace.',
  },
}

export function HelpPanel({
  open,
  place,
  onClose,
  onShortcuts,
  onTour,
  onDocs,
}: {
  open: boolean
  place: Place
  onClose: () => void
  /** The shortcut sheet, which this replaced as the "?" destination. */
  onShortcuts: () => void
  onDocs: () => void
  /**
   * Walk this person around a screen again.
   *
   * A tour is offered once and then never again, which is right the first
   * time and wrong the day somebody wants it back. This is where it lives —
   * the place people look when they are stuck is Help, not a setting.
   */
  onTour: (surface: string) => void
}) {
  const panel = useDialog<HTMLElement>(open, onClose)
  if (!open) return null
  const guidance = ON_THIS_SCREEN[place]
  const guides = guidesFor(place)

  return (
    <div className="scrim" onMouseDown={onClose}>
      <aside
        className="drawer"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Help"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="drawer__head">
          <div>
            <h2 className="drawer__title">Help</h2>
            <p className="drawer__sub">About the screen you are on, and how to get out of it.</p>
          </div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>

        <div className="drawer__body">
          <section className="drawer__block">
            <div className="label">On this screen</div>
            <p className="help__what">{guidance.what}</p>
            <p className="help__row">
              <Icon name="check" size={13} />
              <span>{guidance.next}</span>
            </p>
            <p className="help__row help__row--warn">
              <Icon name="info" size={13} />
              <span>{guidance.gotcha}</span>
            </p>
          </section>

          <section className="drawer__block">
            <div className="label">Guides</div>
            {guides.length > 0 && (
              <div className="help__guides">
                {guides.map((guide) => (
                  <a key={guide.path} href={guideUrl(guide)} target="_blank" rel="noreferrer">
                    <span>{guide.title}</span>
                    <Icon name="chevron-right" size={13} />
                  </a>
                ))}
              </div>
            )}
            <button className="btn btn--tinted btn--sm" onClick={onDocs}>
              <Icon name="callout" size={13} /> Browse all help
            </button>
          </section>

          <section className="drawer__block">
            <div className="label">How this product works</div>
            <p className="help__what">
              You write, and what you write is kept apart from the published site until you publish
              it. There is no separate save step and nothing to name: the first change starts a set
              of changes, the top bar counts them, and Publish puts them in front of readers.
            </p>
            <p className="help__what">
              A publish can be taken back from History, which is the reason it is one button rather
              than a form with a confirmation.
            </p>
          </section>

          {TOURS[TOUR_FOR[place] ?? ''] && (
            <section className="drawer__block">
              <div className="label">Take the tour again</div>
              <p className="help__what">
                {TOURS[TOUR_FOR[place]!]!.stops.length} stops, pointing at the controls on this
                screen. It takes about a minute.
              </p>
              <button className="btn btn--tinted btn--sm" onClick={() => onTour(TOUR_FOR[place]!)}>
                <Icon name="eye" size={13} />
                {TOURS[TOUR_FOR[place]!]!.label}
              </button>
            </section>
          )}

          {/*
            And the ones that belong to a drawer.

            Three tours are about surfaces that are not places — the publish
            sheet, the changes drawer, the inbox — so keying replay off the
            place would leave them offered once and then unreachable for ever,
            which is the one thing worse than a tour nobody asked for. Listed
            here by name, quieter than the one about the screen somebody is
            standing on. A stop whose control is not on the screen still says
            its sentence; the Tour puts it in the middle rather than pinning it
            to a corner.
          */}
          {ELSEWHERE.filter((surface) => surface !== TOUR_FOR[place]).length > 0 && (
            <section className="drawer__block">
              <div className="label">The other walks</div>
              <p className="help__what">
                Short ones, about the panels rather than the screens. Each is offered once and lives
                here afterwards.
              </p>
              <div className="help__tours">
                {ELSEWHERE.filter((surface) => surface !== TOUR_FOR[place]).map((surface) => (
                  <button
                    key={surface}
                    className="btn btn--ghost btn--sm"
                    onClick={() => onTour(surface)}
                  >
                    <Icon name="eye" size={13} />
                    {TOURS[surface]!.label}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/*
            And a person, when none of the above is the answer.
            
            The panel offered explanations, walks and shortcuts and no way to
            reach anybody — which is the one thing somebody opens Help for when
            the screen in front of them is not doing what it says. The screen
            they are on travels with the message, because "which page were you
            on" is the first question support would ask anyway.
          */}
          <section className="drawer__block">
            <div className="label">Still stuck</div>
            <p className="help__what">
              Tell us what happened and we will answer. We reply within a day.
            </p>
            <a
              className="btn btn--tinted btn--sm"
              data-message-us
              href={`mailto:hello@staleless.com?subject=${encodeURIComponent(
                `Help with ${PLACE_NAMES[place]}`,
              )}`}
            >
              <Icon name="callout" size={13} />
              Message us
            </a>
          </section>

          <section className="drawer__block">
            <div className="label">Keyboard</div>
            <p className="help__what">
              <kbd>⌘</kbd> <kbd>K</kbd> goes to any page or runs any command, from anywhere.
            </p>
            <button className="btn btn--tinted btn--sm" onClick={onShortcuts}>
              <Icon name="keyboard" size={13} />
              Every shortcut
            </button>
          </section>
        </div>
      </aside>
    </div>
  )
}
